import { useState } from "react";
import { Plus, Pencil, Trash2, FileText, Loader2, X, Check, Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useNegotiationTemplates, type CreateTemplateInput } from "@/hooks/useNegotiationTemplates";
import type { NegotiationTemplate, TemplateKind } from "@/types/negotiations";

// ---------------------------------------------------------------------------
// Template form
// ---------------------------------------------------------------------------

interface TemplateFormValues {
  name: string;
  kind: TemplateKind;
  tone: string;
  target_discount_pct: string;
  floor_apr: string;
  body_guidance: string;
}

const defaultValues: TemplateFormValues = {
  name: "",
  kind: "outreach",
  tone: "",
  target_discount_pct: "",
  floor_apr: "",
  body_guidance: "",
};

function formToInput(values: TemplateFormValues): CreateTemplateInput {
  return {
    name: values.name.trim(),
    kind: values.kind,
    tone: values.tone.trim() || null,
    target_discount_pct: values.target_discount_pct ? Number(values.target_discount_pct) : null,
    floor_apr: values.floor_apr ? Number(values.floor_apr) : null,
    body_guidance: values.body_guidance.trim() || null,
  };
}

function templateToFormValues(t: NegotiationTemplate): TemplateFormValues {
  return {
    name: t.name,
    kind: t.kind,
    tone: t.tone ?? "",
    target_discount_pct: t.target_discount_pct != null ? String(t.target_discount_pct) : "",
    floor_apr: t.floor_apr != null ? String(t.floor_apr) : "",
    body_guidance: t.body_guidance ?? "",
  };
}

// ---------------------------------------------------------------------------
// Kind badge + metadata
// ---------------------------------------------------------------------------

const kindLabel: Record<TemplateKind, string> = {
  outreach: "Outreach",
  reminder: "Reminder",
  confirmation: "Confirmation",
};

const kindDescription: Record<TemplateKind, string> = {
  outreach: "First contact — sent when you start a new negotiation with a supplier.",
  reminder: "Follow-up — sent when a supplier hasn't responded after a few days.",
  confirmation: "Wrap-up — sent when both sides have agreed on terms.",
};

function KindBadge({ kind }: { kind: TemplateKind }) {
  const variants: Record<TemplateKind, string> = {
    outreach: "bg-primary/10 text-primary border-primary/30",
    reminder: "bg-warning/10 text-warning border-warning/30",
    confirmation: "bg-success/10 text-success border-success/30",
  };
  return (
    <span className={`text-xs border rounded px-1.5 py-0.5 font-medium ${variants[kind]}`}>
      {kindLabel[kind]}
    </span>
  );
}

// ---------------------------------------------------------------------------
// FieldInfo — small info icon with a tooltip explaining a form field
// ---------------------------------------------------------------------------

function FieldInfo({ text }: { text: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="inline-flex items-center text-muted-foreground hover:text-foreground cursor-help"
            aria-label={`Info: ${text}`}
          >
            <Info className="w-3.5 h-3.5" />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs leading-snug">{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Template edit dialog
// ---------------------------------------------------------------------------

interface TemplateDialogProps {
  open: boolean;
  onClose: () => void;
  initial?: NegotiationTemplate;
  onSave: (values: TemplateFormValues) => Promise<void>;
  isSaving?: boolean;
}

function TemplateDialog({ open, onClose, initial, onSave, isSaving }: TemplateDialogProps) {
  const [values, setValues] = useState<TemplateFormValues>(
    initial ? templateToFormValues(initial) : defaultValues
  );

  function set<K extends keyof TemplateFormValues>(key: K, val: TemplateFormValues[K]) {
    setValues((prev) => ({ ...prev, [key]: val }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!values.name.trim()) return;
    await onSave(values);
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit Template" : "New Template"}</DialogTitle>
        </DialogHeader>

        {/* Plain-language explainer shown at the top of the form */}
        <p className="text-xs text-muted-foreground -mt-1 mb-1 leading-relaxed">
          The AI uses these settings to draft an email — you review and approve every draft before
          anything is sent.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor="tmpl-name">Name</Label>
            <Input
              id="tmpl-name"
              value={values.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Standard outreach"
              required
            />
            <p className="text-xs text-muted-foreground">
              A short name so you can find this template quickly.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="tmpl-kind">Kind</Label>
                <FieldInfo
                  text={`• Outreach — ${kindDescription.outreach}\n• Reminder — ${kindDescription.reminder}\n• Confirmation — ${kindDescription.confirmation}`}
                />
              </div>
              <Select value={values.kind} onValueChange={(v) => set("kind", v as TemplateKind)}>
                <SelectTrigger id="tmpl-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="outreach">
                    <span className="font-medium">Outreach</span>
                    <span className="ml-1 text-muted-foreground text-xs">— first contact</span>
                  </SelectItem>
                  <SelectItem value="reminder">
                    <span className="font-medium">Reminder</span>
                    <span className="ml-1 text-muted-foreground text-xs">— follow-up</span>
                  </SelectItem>
                  <SelectItem value="confirmation">
                    <span className="font-medium">Confirmation</span>
                    <span className="ml-1 text-muted-foreground text-xs">— deal agreed</span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">When this template is used.</p>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="tmpl-tone">Tone (optional)</Label>
                <FieldInfo text="How the email should sound — e.g. 'warm and professional' or 'formal'. The AI matches this tone when drafting." />
              </div>
              <Input
                id="tmpl-tone"
                value={values.tone}
                onChange={(e) => set("tone", e.target.value)}
                placeholder="e.g. warm and professional"
              />
              <p className="text-xs text-muted-foreground">Leave blank to use the default tone.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="tmpl-discount">Target Discount % (optional)</Label>
                <FieldInfo text="The discount percentage the AI will aim for when drafting — e.g. 2 means the AI proposes a 2% discount for early payment. The supplier can counter-offer." />
              </div>
              <Input
                id="tmpl-discount"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={values.target_discount_pct}
                onChange={(e) => set("target_discount_pct", e.target.value)}
                placeholder="e.g. 2.0"
              />
              <p className="text-xs text-muted-foreground">Discount % the AI aims to negotiate.</p>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="tmpl-floor">Floor APR % (optional)</Label>
                <FieldInfo text="The minimum annual rate (APR) you're willing to accept. The AI won't agree to terms below this threshold — it's your floor, not a target." />
              </div>
              <Input
                id="tmpl-floor"
                type="number"
                min="0"
                step="0.1"
                value={values.floor_apr}
                onChange={(e) => set("floor_apr", e.target.value)}
                placeholder="e.g. 12"
              />
              <p className="text-xs text-muted-foreground">Minimum APR the AI won't go below.</p>
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="tmpl-guidance">Body Guidance (optional)</Label>
              <FieldInfo text="Plain-English instructions for the AI — e.g. 'Emphasize our long relationship and offer a quick call.' The AI writes the actual email; this just steers it. Keep it brief." />
            </div>
            <textarea
              id="tmpl-guidance"
              className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
              value={values.body_guidance}
              onChange={(e) => set("body_guidance", e.target.value)}
              placeholder="e.g. Emphasize our long relationship and offer a quick call."
            />
            <p className="text-xs text-muted-foreground">
              Plain-English hints for the AI — it writes the email, this just steers it.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || !values.name.trim()}>
              {isSaving ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  Saving…
                </>
              ) : (
                <>
                  <Check className="w-3.5 h-3.5 mr-1.5" />
                  Save
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Delete confirm
// ---------------------------------------------------------------------------

interface DeleteConfirmProps {
  open: boolean;
  templateName: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isDeleting?: boolean;
}

function DeleteConfirm({ open, templateName, onClose, onConfirm, isDeleting }: DeleteConfirmProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete Template</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground mt-1">
          Delete <span className="font-medium text-foreground">{templateName}</span>? This cannot be
          undone.
        </p>
        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isDeleting}>
            {isDeleting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
            ) : (
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            )}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// TermsPill — compact readable display of discount / floor-APR terms
// ---------------------------------------------------------------------------

function TermsPill({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground border border-border/60 rounded px-1.5 py-0.5">
      <span className="font-medium text-foreground/70">{label}</span>
      <span>{value}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export interface TemplatesManagerProps {
  /** Called when user picks a template (e.g. to seed a draft). */
  onSelect?: (template: NegotiationTemplate) => void;
  /** When true, show a compact "Select" action instead of full CRUD. */
  pickMode?: boolean;
}

export function TemplatesManager({ onSelect, pickMode = false }: TemplatesManagerProps) {
  const {
    templates,
    isLoading,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    isCreating,
    isUpdating,
    isDeleting,
  } = useNegotiationTemplates();

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<NegotiationTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<NegotiationTemplate | null>(null);

  async function handleCreate(values: TemplateFormValues) {
    await createTemplate(formToInput(values));
  }

  async function handleEdit(values: TemplateFormValues) {
    if (!editTarget) return;
    await updateTemplate(editTarget.id, formToInput(values));
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    await deleteTemplate(deleteTarget.id);
    setDeleteTarget(null);
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-foreground flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Templates
            </CardTitle>
            {!pickMode && (
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 text-xs"
                onClick={() => setCreateOpen(true)}
                title="Create new template"
              >
                <Plus className="w-3.5 h-3.5" />
                New
              </Button>
            )}
          </div>
          {/* Plain-language explanation of what templates are */}
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Templates guide the AI&apos;s tone and terms when it drafts outreach emails — you stay
            in control and approve every message before it sends.
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-20">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          ) : templates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 gap-2 text-center">
              <FileText className="w-10 h-10 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No templates yet.</p>
              <p className="text-xs text-muted-foreground max-w-xs">
                Create a template to give the AI a starting point — set the tone, target discount,
                and any guidance you want it to follow.
              </p>
              {!pickMode && (
                <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)}>
                  <Plus className="w-3.5 h-3.5 mr-1.5" />
                  Create your first template
                </Button>
              )}
            </div>
          ) : (
            <ul className="space-y-2">
              {templates.map((t) => (
                <li
                  key={t.id}
                  className="flex items-start gap-3 px-3 py-2.5 rounded-md border border-border/60 bg-muted/20 hover:bg-muted/40 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    {/* Row 1: name + kind badge + tone */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-foreground truncate">{t.name}</span>
                      <KindBadge kind={t.kind} />
                      {t.tone && (
                        <Badge variant="secondary" className="text-xs">
                          {t.tone}
                        </Badge>
                      )}
                    </div>

                    {/* Row 2: kind description + terms */}
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {kindDescription[t.kind]}
                    </p>

                    {/* Row 3: numeric terms pills */}
                    {(t.target_discount_pct != null || t.floor_apr != null) && (
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        {t.target_discount_pct != null && (
                          <TermsPill label="Target discount:" value={`${t.target_discount_pct}%`} />
                        )}
                        {t.floor_apr != null && (
                          <TermsPill label="Floor APR:" value={`${t.floor_apr}%`} />
                        )}
                      </div>
                    )}

                    {/* Row 4: body guidance preview */}
                    {t.body_guidance && (
                      <p
                        className="text-xs text-muted-foreground mt-0.5 truncate italic"
                        title={t.body_guidance}
                      >
                        AI hint: {t.body_guidance}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-1 shrink-0 mt-0.5">
                    {pickMode && onSelect && (
                      <Button
                        size="sm"
                        variant="default"
                        className="h-7 text-xs"
                        onClick={() => onSelect(t)}
                      >
                        Use
                      </Button>
                    )}
                    {!pickMode && (
                      <>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => setEditTarget(t)}
                          title="Edit template"
                          aria-label="Edit template"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => setDeleteTarget(t)}
                          title="Delete template"
                          aria-label="Delete template"
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      {createOpen && (
        <TemplateDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onSave={handleCreate}
          isSaving={isCreating}
        />
      )}

      {/* Edit dialog */}
      {editTarget && (
        <TemplateDialog
          open={!!editTarget}
          onClose={() => setEditTarget(null)}
          initial={editTarget}
          onSave={handleEdit}
          isSaving={isUpdating}
        />
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <DeleteConfirm
          open={!!deleteTarget}
          templateName={deleteTarget.name}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
}
